import * as ko from "knockout";

import { Bounds, FeatureType, Feature, LatLng, Layer, MapAdapter, MapEvent } from "./api";



/**
 * Options for MapViewModel.
 */
export interface MapViewModelOptions {
    colors?: string[];
    onFullscreenToggle?: (isFullscreen: boolean) => void;
    onMarkerClick?: (e: MapEvent) => void;
    onResetFocus?: () => void;
    showFullscreenButton?: boolean;
}

/**
 * View model for a map instance.
 */
export class MapViewModel {
    bounds: ko.Computed<Bounds>;
    colors: string[];
    drawnObjects: ko.Computed<GeoJSON.FeatureCollection<GeoJSON.GeometryObject>>;
    latLong: ko.Computed<LatLng>;
    zoom: ko.Computed<number>;

    layers: ko.Observable<Layer[]>;
    adapter: ko.Observable<MapAdapter>;

    onMarkerClick: (e: MapEvent) => void;
    onResetFocus: () => void;
    showFullscreenButton: boolean;
    dialogContent: ko.Observable<string>;
    dialogVisible: ko.Observable<boolean>;
    onFullscreenToggle: (isFullscreen: boolean) => void;

    /**
     * Creates a new MapViewModel instance.
     */
    constructor (properties: MapViewModelOptions = {}) {
        this.adapter = ko.observable<MapAdapter>(null); // initialized by binding
        this.layers = ko.observable<Layer[]>([]);

        this.bounds = ko.pureComputed(() => {
            const adapter = this.adapter();
            return adapter ? adapter.bounds() : null;
        });
        this.drawnObjects = ko.pureComputed(() => {
            const adapter = this.adapter();
            return adapter ? adapter.drawnObjects() : null;
        });
        this.latLong = ko.pureComputed(() => {
            const adapter = this.adapter();
            return adapter ? adapter.latLong() : null;
        });
        this.zoom = ko.pureComputed(() => {
            const adapter = this.adapter();
            return adapter ? adapter.zoom() : null;
        });

        this.colors = properties.colors || [
            "#5DA5DA", "#FAA43A", "#60BD68", "#F17CB0",
            "#B2912F", "#B276B2", "#DECF3F", "#F15854",
            "#4D4D4D"
        ];

        this.dialogContent = ko.observable<string>(null);
        this.dialogVisible = ko.observable<boolean>(false);

        this.onResetFocus = properties.onResetFocus;
        this.showFullscreenButton = properties.showFullscreenButton || false;
        this.onFullscreenToggle = properties.onFullscreenToggle;
        this.onMarkerClick = properties.onMarkerClick;
    }

    /**
     * Centers the map on a given feature and optionally zooms to the bounds of the feature.
     */
    center(feature: (Feature | FeatureType), zoomToBounds: boolean): void {
        const adapter = this.adapter();
        if (!adapter) {
            throw new Error("MapViewModel instance must be bound to a map.");
        }
        adapter.center(feature, zoomToBounds);
    }

    /**
     * Removes the feature layer of the specified type or all if no type specified.
     */
    clearFeatures(type?: FeatureType): void {
        this.layers(type !== undefined
            ? this.layers().filter(layer => layer.featureType !== type)
            : []
        );
    }

    /**
     * Returns true if a layer with the given feature type has been added to this map.
     */
    hasFeatures(type: FeatureType): boolean {
        return this.layers().filter(layer => layer.featureType === type).length > 0;
    }

    /**
     * Gets the current collection of land features and their associated properties, optionally filtering by a predicate on those properties.
     */
    getLandFeatures<TProperties>(propFilter?: (a: TProperties) => boolean): Feature[] {
        const adapter = this.adapter();
        if (!adapter) {
            throw new Error("MapViewModel instance must be bound to a map.");
        }
        return adapter.getFeatures(FeatureType.Land, propFilter);
    }

    /**
     * Add or replace land features.
     */
    setLandFeatures(geoData: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>, featureAddCallback?: (f: Feature) => void): void {
        if (global.console && global.console.group) {
            global.console.group("land feature layer");
            global.console.log(geoData);
        }

        const oldLayers = this.layers.peek();
        const newLayers = oldLayers.filter(l => l.featureType !== FeatureType.Land)
            .concat({ featureType: FeatureType.Land, geoData, featureAddCallback });

        this.layers(newLayers);

        if (global.console && global.console.groupEnd) {
            global.console.groupEnd();
        }
    }

    /**
     * Add or replace well features.
     */
    setWellFeatures(geoData: GeoJSON.FeatureCollection<GeoJSON.GeometryObject> | any[], featureAddCallback?: (f: Feature) => void): void {
        if (global.console && global.console.group) {
            global.console.group("well feature layer");
            global.console.log(geoData);
        }

        const oldLayers = this.layers.peek();
        const newLayers = oldLayers.filter(l => l.featureType !== FeatureType.Well)
                .concat({ featureType: FeatureType.Well, geoData, featureAddCallback });

        this.layers(newLayers);

        if (global.console && global.console.groupEnd) {
            global.console.groupEnd();
        }
    }
}



// WEBPACK FOOTER //
// mapViewModel.ts